<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between" cols="12">
                <h1 class="text-left">Discount Codes</h1>
                <v-btn color="black" prepend-icon="mdi-plus" @click="dialogSwitch = true">Add</v-btn>
            </v-col>
        </v-row>
        <h3></h3>
        <v-row>
            <v-col cols="12">
                <v-table>
                    <thead>
                    <tr>
                        <th>Code</th>
                        <th>Type</th>
                        <th>Scheme</th>
                        <th>Max Allowed Usage</th>
                        <th>Max Allowed Usage per Company</th>
                        <th>Current Usage</th>
                        <th>Requires New Device</th>
                        <th>Requires New Company</th>
                        <th>Auto Apply</th>
                        <th>Status</th>
                        <th>Valid From</th>
                        <th>Valid Until</th>
                        <th>Created At</th>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="discount_code in discount_codes" v-if="discount_codes.length"
                        :key="discount_code.id" class="text-left">
                        <td>{{ discount_code.code }}</td>
                        <td>{{ getCodeType(discount_code.type) }}</td>
                        <td>{{ discount_code.discount_scheme?.name }}</td>
                        <td>{{ discount_code.max_allowed_usage }}</td>
                        <td>{{ discount_code.max_allowed_usage_per_company }}</td>
                        <td>{{ discount_code.current_usage }}</td>
                        <td>
                            <v-icon :icon="discount_code.requires_new_device?'mdi-check-circle':'mdi-close-circle'"/>
                        </td>
                        <td>
                            <v-icon :icon="discount_code.requires_new_company?'mdi-check-circle':'mdi-close-circle'"/>
                        </td>
                        <td>
                            <v-icon :icon="discount_code.auto_apply?'mdi-check-circle':'mdi-close-circle'"/>
                        </td>
                        <td>
                            <v-icon :icon="discount_code.is_active?'mdi-check-circle':'mdi-close-circle'"/>
                        </td>
                        <td>{{ new Date(discount_code.valid_from).toLocaleDateString() }}</td>
                        <td>{{ new Date(discount_code.valid_until).toLocaleDateString() }}</td>
                        <td>{{ formatDateTime(discount_code.created_at) }}</td>
                        <td>
                            <v-btn-group density="compact" divided variant="outlined">
                                <v-btn :disabled="editDialogSwitch" class="text-warning" prepend-icon="mdi-pencil"
                                       size="x-small" @click="editDiscountCode(discount_code)">Edit
                                </v-btn>
                                <v-btn class="text-error" prepend-icon="mdi-delete" size="x-small"
                                       @click="deleteDiscountCode(discount_code)">Delete
                                </v-btn>
                            </v-btn-group>
                        </td>
                    </tr>
                    <tr v-else>
                        <td v-if="loading" colspan="9">
                            <v-progress-circular color="primary" indeterminate/>
                        </td>
                        <td v-else colspan="9">No record found</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-col>
        </v-row>

        <v-row v-if="totalPages > 1" justify="center">
            <v-col cols="8">
                <v-pagination
                    v-model="currentPage"
                    :length="totalPages"
                    class="my-4"
                    density="compact"
                    @update:model-value="fetchDiscountCodes"
                ></v-pagination>
            </v-col>
        </v-row>
    </v-container>

    <create-edit-discount-code v-if="dialogSwitch" v-model="dialogSwitch"
                               :discount_code="selectedDiscountCode"
                               @addDiscountCode="addDiscountCode"
                               @updateDiscountCode="updateDiscountCode"
                               @update:model-value="function (val){if(!val){ selectedDiscountCode = null}}"/>
</template>

<script setup>
import {onMounted, ref} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {formatDateTime} from '@/composables/useTimezone';
import CreateEditDiscountCode from "@/components/dialogs/discount-codes/Create_Edit.vue";

const loading = ref(true);
const totalPages = ref(1);
const currentPage = ref(1);
const discount_codes = ref([]);
const {showSnackbar} = useSnackbarStore();
const dialogSwitch = ref(false);
const editDialogSwitch = ref(false);
const selectedDiscountCode = ref(null);
const DiscountCodeTypes = [
    {value: 1, title: 'Universal'},
    {value: 2, title: 'Device'},
    {value: 3, title: 'Company'},
    {value: 4, title: 'Product sku'},
    {value: 5, title: 'Device Group'},
    {value: 6, title: 'Subscription Plan'},
];

const getCodeType = (_type) => {
    return DiscountCodeTypes.find((type) => type.value === _type).title
}
onMounted(() => {
    fetchDiscountCodes();
});
const fetchDiscountCodes = () => {
    send('get', `admin/discount-codes`)
        .then((response) => {
            discount_codes.value = response.data.data.data;
            totalPages.value = response.data.data.last_page;
            currentPage.value = response.data.data.current_page;
            loading.value = false;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Discount codes could not be loaded', 'error')
        })
}
const addDiscountCode = (discount_code) => {
    discount_codes.value.unshift(discount_code);
}
const editDiscountCode = (discount_code) => {
    selectedDiscountCode.value = discount_code;
    dialogSwitch.value = true;
}
const updateDiscountCode = (discount_code) => {
    selectedDiscountCode.value = null;
    discount_codes.value.map((item, index) => {
        if (item.id === discount_code.id) {
            discount_codes.value[index] = discount_code;
        }
    });
}
const deleteDiscountCode = (discount_code) => {
    if (confirm(`Are you sure to delete "${discount_code.code}" discount code?`)) {
        send('delete', `admin/discount-codes/${discount_code.id}`)
            .then((response) => {
                if (response.data.success) {
                    discount_codes.value.map((item, index) => {
                        if (item.id === discount_code.id) {
                            discount_codes.value.splice(index, 1);
                        }
                    });
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                if (error.response.data.message) {
                    showSnackbar(error.response.data.message, 'error')
                } else {
                    showSnackbar(`"${discount_code.code}" discount code could not be deleted`, 'error')
                }
            })
    }
}
</script>
